import { useCallback, useMemo, useState } from 'react';
import {
	Checkmark,
	Chevron,
	Clock,
	Envelope,
	EnvelopeExclamation,
	EnvelopeFire,
	EnvelopeOpen,
	EnvelopeScheduledSend,
	PlaneTransparent,
	RemoveButton,
	Trash,
	ArrowDown,
	LeftArrowIcon,
	PGArrowRight,
	RightArrowIcon
} from '@/resources/assets/images';
import { useVirtualElements } from '@/hooks/useVirtualElements';
import { Table } from '@/components/atoms/Table';
import { useTable, flexRender, createColumnHelper } from '@/hooks/useTable';

import type {
	Business,
	BusinessQuery
} from '@/modules/dashboard/dashboardTypes';
import { Select } from '@/components/atoms/Select';
import { Tooltip } from '@/components/atoms/Tooltip';
import Loader from '@/components/Loader/Loader';
import dayjs from 'dayjs';
import { Dialog } from '@/components/atoms/Dialog';
import { Input } from '@/components/atoms/Input';
import { sendAnalytics } from '@/services/analyticsService';
import RichEditor from '@/components/RichEditor/RichEditor';
import { Popover } from '@/components/atoms/Popover';
import Button from '@/components/Button/newButtons';
import Link from '@/components/Link/Link';
import type { PanelStepsType } from '@/services/sequencesService';
import { useQueryClient } from '@tanstack/react-query';
import { useSaveNoteMutation } from '../../../hooks/useSaveNoteMutation';
import { useSelectBusinessMutation } from '../../../hooks/useSelectBusinessMutation';
import { useSelectedBusinessData } from '../../../hooks/useSelectedBusinessData';
import { useListDetailsFiltersContext } from '../../../providers/ListDetailsFiltersProviders';
import { useEmailActivity } from './src/hooks/useEmailActivity';
import { useQueryBusinessSequence } from './src/hooks/useQueryBusinessSequence';
import { useUserData } from '../../../hooks/useUserData';

const formatCategoriesList = (categoriesString: string) => {
	const replaceSymbolsByText = categoriesString.replace('&apos;', "'");
	const replaceEscapeBySpace = replaceSymbolsByText.replace(/\n/g, ', ');
	return replaceEscapeBySpace;
};

export type BusinessTableProps = {
	pageData: Array<Business>;
	isLoading: boolean;
	onRemoveBusiness: (businessId: string) => void;
};

export const BusinessTable = ({
	pageData,
	isLoading,
	onRemoveBusiness
}: BusinessTableProps) => {
	const { appliedFilters, onFiltersChange } = useListDetailsFiltersContext();

	const { mutate: mutateSaveNote } = useSaveNoteMutation();

	const { selectedBusiness } = useSelectedBusinessData();
	const { mutate: mutateSelectBusiness } = useSelectBusinessMutation();

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalBusinessData, setModalBusinessData] = useState<Business | null>(
		null
	);

	const { user } = useUserData();

	const queryClient = useQueryClient();

	const handleUpdateLeadValue = useCallback(
		(business: Business, value: boolean | undefined) => {
			mutateSaveNote(business._id, business.name, {
				...business,
				lead: value
			});

			queryClient.invalidateQueries([
				'list-details-business-data',
				{ ...appliedFilters }
			]);

			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User updated lead value in list',
				properties: {
					businessId: business._id,
					newLeadValue: value,
					businessName: business.name
				}
			});
		},
		[appliedFilters, mutateSaveNote, queryClient, user.email]
	);

	const handleOpenEmailModal = () => {
		setIsModalOpen(true);
	};

	const handleCloseEmailModal = () => {
		setIsModalOpen(false);
	};

	const handleEnvelopeClick = useCallback(
		(business: Business) => {
			handleOpenEmailModal();
			mutateSelectBusiness(business);
			setModalBusinessData(business);
		},
		[mutateSelectBusiness]
	);

	const handleRowClick = (business: Business) => {
		if (selectedBusiness?._id && business._id === selectedBusiness?._id) {
			return mutateSelectBusiness(null);
		}

		mutateSelectBusiness(business);
	};

	const handleSort = (header: string) => {
		const normalizedHeader = header.toLowerCase();

		const newSort: BusinessQuery['sort'] = {
			[normalizedHeader]: appliedFilters?.sort?.[normalizedHeader] || '1'
		};

		onFiltersChange({
			limit: 50,
			skip: 0,
			...appliedFilters,
			sort: newSort
		});
	};

	const columns = useMemo(() => {
		const columnHelper =
			createColumnHelper<BusinessTableProps['pageData'][number]>();

		return [
			columnHelper.accessor('name', {
				header: () => (
					<span className="font-barlow text-sm font-bold">
						Name & Email status
					</span>
				),
				cell: ({ getValue, row }) => (
					<Table.CellButton className="inline-flex w-full items-center justify-between gap-4 font-barlow text-sm">
						<Tooltip.Provider>
							<Tooltip.Root>
								<Tooltip.Trigger asChild>
									<button
										type="button"
										onClick={() =>
											mutateSelectBusiness(row.original)
										}
										className="block w-full overflow-hidden text-ellipsis whitespace-nowrap text-left font-barlow text-sm"
									>
										{getValue()}
									</button>
								</Tooltip.Trigger>

								<Tooltip.Content>{getValue()}</Tooltip.Content>
							</Tooltip.Root>
						</Tooltip.Provider>

						<EnvelopeIcon
							onEnvelopeClick={handleEnvelopeClick}
							{...row.original}
						/>

						<SequenceStatus {...row.original} />
					</Table.CellButton>
				),
				minSize: 220
			}),
			columnHelper.accessor('id', {
				header: () => (
					<span className="font-barlow text-sm font-bold">
						Remove
					</span>
				),
				cell: ({ row }) => (
					<Table.CellButton>
						<RemoveBusinessModal
							businessId={row.original._id}
							businessName={row.original.name}
							onRemoveBusiness={onRemoveBusiness}
						/>
					</Table.CellButton>
				),
				size: 80
			}),
			columnHelper.accessor('score', {
				header: () => (
					<span className="font-barlow text-sm font-bold">
						RE2 Score
					</span>
				),
				cell: ({ getValue }) => (
					<span className="font-barlow text-sm">
						{Math.floor(getValue() || 0)}
					</span>
				),
				size: 80
			}),
			columnHelper.accessor('categories', {
				header: () => (
					<span className="font-barlow text-sm font-bold">
						Categories
					</span>
				),
				cell: ({ getValue }) => {
					const displayValue = getValue()?.display || '';

					return (
						<Tooltip.Provider>
							<Tooltip.Root>
								<Tooltip.Trigger asChild>
									<span className="block w-full overflow-hidden text-ellipsis whitespace-nowrap font-barlow text-sm">
										{formatCategoriesList(displayValue)}
									</span>
								</Tooltip.Trigger>

								<Tooltip.Content>
									{formatCategoriesList(displayValue)}
								</Tooltip.Content>
							</Tooltip.Root>
						</Tooltip.Provider>
					);
				}
			}),
			columnHelper.accessor('locations', {
				header: () => (
					<span className="font-barlow text-sm font-bold">
						Locations
					</span>
				),
				cell: ({ getValue }) => {
					const isTenOrMore = getValue() >= 10;

					if (isTenOrMore) {
						return <span className="font-barlow text-sm">10+</span>;
					}

					const isOne = getValue() === 1;

					if (isOne) {
						return <span className="font-barlow text-sm">1</span>;
					}

					return <span className="font-barlow text-sm">2-10</span>;
				}
			}),
			columnHelper.accessor('contactNames.name', {
				header: () => (
					<span className="font-barlow text-sm font-bold">
						Contacted
					</span>
				),
				cell: ({ row }) => {
					if (!row.original?.isContacted) return null;

					return (
						<button type="button">
							<Checkmark />
						</button>
					);
				},
				size: 80
			}),
			columnHelper.accessor('lead', {
				header: () => (
					<span className="font-barlow text-sm font-bold">Leads</span>
				),
				cell: ({ getValue, row }) => {
					const rawValue = getValue();
					const noValue = rawValue === undefined;
					let value;

					if (noValue) {
						value = 'lead-status';
					} else if (rawValue) {
						value = 'lead';
					} else {
						value = 'no-lead';
					}

					const handleValueChange = (newValue: string) => {
						let updatedValue;

						if (newValue === 'lead') {
							updatedValue = true;
						} else if (newValue === 'no-lead') {
							updatedValue = false;
						} else {
							updatedValue = undefined;
						}

						handleUpdateLeadValue(row.original, updatedValue);
					};

					return (
						<Select.Root
							value={noValue ? undefined : value}
							onValueChange={handleValueChange}
						>
							<Select.Trigger
								className="w-full rounded-md border-2 p-2 data-[status=lead]:border-dm-green data-[status=no-lead]:border-dm-red"
								data-status={value}
							>
								<Select.Value placeholder="Lead Status" />
							</Select.Trigger>
							<Select.Content>
								<Select.Item value="lead-status">
									Lead Status
								</Select.Item>
								<Select.Item value="lead">Lead</Select.Item>
								<Select.Item value="no-lead">
									No Lead
								</Select.Item>
							</Select.Content>
						</Select.Root>
					);
				},

				size: 120
			})
		];
	}, [
		handleEnvelopeClick,
		handleUpdateLeadValue,
		mutateSelectBusiness,
		onRemoveBusiness
	]);

	const { rows, headers } = useTable({
		data: pageData,
		columns,
		getRowId: ({ _id }) => _id
	});

	const { containerRef, virtualRows, paddingTop, paddingBottom } =
		useVirtualElements<HTMLDivElement>({
			count: rows.length ?? 0,
			estimateSize: () => 40,
			overscan: 15,
			scrollMargin: 150
		});

	if (isLoading) {
		return (
			<div className="flex h-full w-full items-center justify-center">
				<Loader />
			</div>
		);
	}

	const sortableColumns = ['name', 'categories', 'locations', 'score'];

	return (
		<>
			<Table.Root ref={containerRef} className="relative min-h-[50dvh]">
				<Table.Header className="group">
					{headers.map(headerGroup => (
						<Table.Row key={headerGroup.id} className="bg-dm-black">
							{headerGroup.headers.map(header => {
								const isSortable = sortableColumns.includes(
									header.column.id
								);

								const isActiveSort =
									header.column.id in
									(appliedFilters?.sort || {});

								const sortDirection =
									appliedFilters?.sort?.[header.column.id] ??
									null;

								return (
									<Table.Head
										key={header.id}
										className="relative"
										style={{
											width: header.getSize() || '100%'
										}}
									>
										<button
											type="button"
											data-sortable={isSortable}
											className="group flex w-full items-center data-[sortable=true]:cursor-pointer data-[sortable=true]:justify-between"
											onClick={() =>
												isSortable &&
												handleSort(header.column.id)
											}
										>
											<span className="font-barlow text-sm font-bold">
												{flexRender(
													header.column.columnDef
														.header,
													header.getContext()
												)}
											</span>

											{isSortable && (
												<div
													data-active-sort={
														isActiveSort
													}
													className="absolute right-2 flex gap-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100 data-[active-sort=true]:opacity-100"
												>
													<Chevron
														data-rotated={
															sortDirection !==
															'-1'
														}
														className="fill-white transition-transform data-[rotated=true]:rotate-180"
													/>
												</div>
											)}
										</button>
									</Table.Head>
								);
							})}
						</Table.Row>
					))}
				</Table.Header>

				<Table.Body
					columns={columns.length}
					paddingTop={paddingTop}
					paddingBottom={paddingBottom}
				>
					{virtualRows?.length ? (
						<>
							{virtualRows.map(({ index }) => {
								const row = rows[index];

								if (!row) {
									return null;
								}

								return (
									<Table.Row
										key={row.id}
										isSelected={
											selectedBusiness?._id === row.id
										}
										onClick={() =>
											handleRowClick(row.original)
										}
									>
										{row.getVisibleCells().map(cell => {
											return (
												<Table.Cell
													key={cell.id}
													style={{
														width:
															cell.column.getSize() ||
															'100%'
													}}
												>
													{flexRender(
														cell.column.columnDef
															.cell,
														cell.getContext()
													)}
												</Table.Cell>
											);
										})}
									</Table.Row>
								);
							})}
						</>
					) : (
						<Table.Row>
							<Table.Cell
								colSpan={columns.length}
								className="h-24 text-center"
							>
								<div className="m-6 flex flex-col items-start sm:items-center">
									No Results
								</div>
							</Table.Cell>
						</Table.Row>
					)}
				</Table.Body>
			</Table.Root>

			<EmailModal
				name={modalBusinessData?.name || ''}
				emailSubject={modalBusinessData?.emailSubject || ''}
				emailBody={modalBusinessData?.emailBody || ''}
				isModalOpen={isModalOpen}
				onModalClose={handleCloseEmailModal}
			/>
		</>
	);
};

const envelopeMap: Record<
	Business['emailStatus'],
	{
		Content: ({
			emailStatusCount
		}: Pick<Business, 'emailStatusCount'>) => JSX.Element;
		TooltipContent: ({
			emailStatusCount
		}: Pick<Business, 'emailStatusCount'>) => JSX.Element;
	}
> = {
	sent: {
		Content: () => <Envelope className="block size-full text-dark-grey" />,
		TooltipContent: () => <>Email hasn&apos;t been opened yet</>
	},
	waiting: {
		Content: () => <Envelope className="block size-full text-dark-grey" />,
		TooltipContent: () => <>Email hasn&apos;t been opened yet</>
	},
	created: {
		Content: () => <Envelope className="block size-full text-dark-grey" />,
		TooltipContent: () => <>Email hasn&apos;t been opened yet NYLAS.</>
	},
	blocked: {
		Content: () => (
			<EnvelopeExclamation className="block size-full text-dark-grey" />
		),
		TooltipContent: () => <>Email blocked.</>
	},
	bounce: {
		Content: () => (
			<EnvelopeExclamation className="block size-full text-dark-grey" />
		),
		TooltipContent: () => <>Email bounced.</>
	},
	scheduled: {
		Content: () => <Clock className="block size-full text-dark-grey" />,
		TooltipContent: () => <>Email scheduled to be sent.</>
	},
	queued: {
		Content: () => <Clock className="block size-full text-dark-grey" />,
		TooltipContent: () => <>Email queued to send.</>
	},
	failed: {
		Content: () => (
			<EnvelopeExclamation className="block size-6 text-dark-grey" />
		),
		TooltipContent: () => <>Email failed to send.</>
	},
	open: {
		Content: ({ emailStatusCount }: Pick<Business, 'emailStatusCount'>) => {
			if (emailStatusCount >= 4) {
				return (
					<>
						<EnvelopeFire className="block size-full text-dark-grey" />
						<span className="envelope-counter absolute">
							{emailStatusCount > 9 ? '9+' : emailStatusCount}
						</span>
					</>
				);
			}
			return (
				<>
					<EnvelopeOpen className="block size-full text-dark-grey" />
					{emailStatusCount && (
						<span className="envelope-counter absolute">
							{emailStatusCount}
						</span>
					)}
				</>
			);
		},
		TooltipContent: ({
			emailStatusCount
		}: Pick<Business, 'emailStatusCount'>) => {
			if (emailStatusCount > 1) {
				return (
					<>
						Email has been opened{' '}
						{emailStatusCount > 9 ? '9+' : emailStatusCount} times.
					</>
				);
			}

			return <>Email has been opened 1 time.</>;
		}
	}
};

type EnvelopeIconProps = Business & {
	onEnvelopeClick: (business: Business) => void;
};

const EnvelopeIcon = ({ onEnvelopeClick, ...business }: EnvelopeIconProps) => {
	const { _id, emailStatus = '', emailStatusCount } = business;

	const { scheduledEmailActivity } = useEmailActivity();

	if (!emailStatus || !(emailStatus in envelopeMap)) return null;

	const { Content, TooltipContent } = envelopeMap[emailStatus];

	const handleClickBusinessEnvelope = () => {
		onEnvelopeClick(business);
	};

	return (
		<Tooltip.Provider>
			<Tooltip.Root>
				<Tooltip.Trigger asChild>
					<button
						type="button"
						className="relative flex aspect-square size-6 items-center justify-center p-0.5"
						onClick={handleClickBusinessEnvelope}
					>
						{scheduledEmailActivity[_id] ? (
							<EnvelopeScheduledSend className="text-dark-grey" />
						) : (
							<Content emailStatusCount={emailStatusCount} />
						)}
					</button>
				</Tooltip.Trigger>

				<Tooltip.Content>
					{scheduledEmailActivity[_id] ? (
						<>
							Email has been scheduled at:
							{dayjs(
								scheduledEmailActivity[_id].scheduleDate,
								'MM/DD/YYYY, h:mm:ss A'
							)?.format('ddd, MMM D')}{' '}
							at
							{dayjs(
								scheduledEmailActivity[_id].scheduleDate,
								'MM/DD/YYYY, h:mm:ss A'
							)?.format('hh:mm a')}
						</>
					) : (
						<TooltipContent emailStatusCount={emailStatusCount} />
					)}
				</Tooltip.Content>
			</Tooltip.Root>
		</Tooltip.Provider>
	);
};

const SequenceStatus = ({ ...business }: Business) => {
	const { hasActiveCampaign, _id } = business;

	const [stepIndex, setStepIndex] = useState(0);
	const [sequenceIndex, setSequenceIndex] = useState(0);

	const { data = null } = useQueryBusinessSequence({
		businessId: _id,
		enabled: hasActiveCampaign
	});

	if (!data) return null;

	const { campaigns } = data?.data.data;

	const currentSequence = campaigns[sequenceIndex];
	const canGoNextStep = stepIndex < currentSequence.upcomingSteps.length - 1;
	const canGoPrevStep = stepIndex > 0;

	const orderOfFirst = currentSequence.currentStep.order ?? 0;

	const handlePrevStep = () => {
		if (!canGoPrevStep) return;

		setStepIndex(state => {
			return state - 1;
		});
	};

	const handleNextStep = () => {
		if (!canGoNextStep) return;

		setStepIndex(state => {
			return state + 1;
		});
	};

	return (
		<Popover.Root>
			<Popover.Trigger className="w-fit border-none ring-0 focus:ring-0 data-[state='closed']:text-dm-strong-mid-grey data-[state='open']:text-dm-gold">
				<PlaneTransparent />
			</Popover.Trigger>

			<Popover.Content
				className="h-fit w-fit rounded-2xl border-none p-0"
				side="right"
				align="start"
				sideOffset={15}
				alignOffset={-12}
				collisionPadding={92}
			>
				<div className="rounded-2xl border border-dm-gray px-6 py-5">
					<section className="mb-4 flex items-center justify-between gap-4">
						<div className="mr-auto grid grid-cols-[auto_1fr]">
							<span className="mr-4 text-base font-semibold text-white">
								Sequence Status of:
							</span>

							{campaigns.length > 1 ? (
								<Popover.Root>
									<Popover.Trigger className="group/popover h-auto rounded-none border-none p-0 text-base font-bold text-dm-gold outline-none ring-0 duration-300 hover:text-orange-brighter hover:outline-none hover:ring-0 focus:outline-none focus:ring-0 data-[state='opened']:text-orange-brighter">
										{currentSequence.name}
										<ArrowDown className="ml-2 duration-200 group-data-[state=open]/popover:rotate-180" />
									</Popover.Trigger>

									<Popover.Content
										className="flex min-w-[9.375rem] max-w-max flex-col p-1"
										sideOffset={10}
									>
										{campaigns.map((campaign, index) => (
											<div
												key={campaign.campaignId}
												onClick={() => {
													setSequenceIndex(index);
													setStepIndex(0);
												}}
												onKeyDown={e => {
													if (e.key === 'Enter') {
														setSequenceIndex(index);
														setStepIndex(0);
													}
												}}
												role="button"
												tabIndex={0}
												data-current-campaign={
													campaign.campaignId ===
													currentSequence.campaignId
												}
												className="rounded-sm p-1 duration-300 hover:brightness-90 data-[current-campaign=true]:pointer-events-none data-[current-campaign=true]:bg-dm-black3.5 data-[current-campaign=false]:hover:bg-dm-black3.5 data-[current-campaign=false]:focus:bg-dm-black3.5"
											>
												{campaign.name}
											</div>
										))}
									</Popover.Content>
								</Popover.Root>
							) : (
								<div className="text-base font-bold text-dm-gold">
									{currentSequence.name}
								</div>
							)}
						</div>

						{currentSequence.upcomingSteps.length > 1 && (
							<div className="mr-10 flex gap-1">
								{Array.from({
									length: currentSequence.upcomingSteps.length
								}).map((_, i) => (
									<div
										// eslint-disable-next-line react/no-array-index-key
										key={i}
										data-current-step={i === stepIndex}
										className="h-1 w-1 rounded-full data-[current-step=false]:bg-dark-grey data-[current-step=true]:bg-orange"
									/>
								))}
							</div>
						)}
					</section>

					<section className="flex h-full gap-3">
						{currentSequence.upcomingSteps.length > 1 && (
							<button
								type="button"
								disabled={!canGoPrevStep}
								onClick={handlePrevStep}
								className="text-orange disabled:cursor-not-allowed disabled:text-dm-gray"
							>
								<LeftArrowIcon className="h-[1.375rem] w-[1.375rem]" />
							</button>
						)}

						<div className="my-auto w-full overflow-hidden">
							<div
								style={{
									transform: `translate(${`${51 * -stepIndex}%`}, 0)`
								}}
								className="flex h-full w-full max-w-[32.5rem] gap-4 transition-transform duration-300 ease-out"
							>
								<StepCard
									step={currentSequence?.currentStep}
									order={orderOfFirst + 1}
									current
								/>

								{currentSequence.upcomingSteps.map(
									(step, index) => (
										<StepCard
											key={step.stepId}
											step={step}
											order={orderOfFirst + index + 2}
										/>
									)
								)}
							</div>
						</div>

						{currentSequence.upcomingSteps.length > 1 && (
							<button
								type="button"
								disabled={!canGoNextStep}
								onClick={handleNextStep}
								className="text-orange disabled:cursor-not-allowed disabled:text-dm-gray"
							>
								<RightArrowIcon className="h-[1.375rem] w-[1.375rem]" />
							</button>
						)}
					</section>

					<section className="mt-4 flex justify-end">
						<Link to="/sequences">
							<Button
								variant="text"
								className="px-0"
								rightIcon={<PGArrowRight />}
							>
								More details
							</Button>
						</Link>
					</section>
				</div>
			</Popover.Content>
		</Popover.Root>
	);
};

type EmailModalProps = Pick<Business, 'emailSubject' | 'emailBody' | 'name'> & {
	isModalOpen: boolean;
	onModalClose: (open: boolean) => void;
};

const EmailModal = ({
	name,
	emailSubject,
	emailBody,
	isModalOpen,
	onModalClose
}: EmailModalProps) => (
	<Dialog.Root open={isModalOpen} onOpenChange={onModalClose}>
		<Dialog.Content>
			<Dialog.Header>
				<Dialog.Title>Email {name}</Dialog.Title>
			</Dialog.Header>

			<div className="flex flex-col gap-4">
				<section className="flex flex-col gap-2">
					<header>
						<p>Subject:</p>
					</header>

					<Input
						value={emailSubject}
						readOnly
						color="contrast"
						rounded="md"
					/>
				</section>

				<section className="flex flex-col gap-2">
					<header>
						<p>Message:</p>
					</header>

					<RichEditor
						value={emailBody}
						onChange={() => null}
						readOnly
						className="h-full max-h-52 rounded-md bg-dm-white text-dm-black"
					/>
				</section>
			</div>
		</Dialog.Content>
	</Dialog.Root>
);

type RemoveBusinessModalProps = Pick<BusinessTableProps, 'onRemoveBusiness'> & {
	businessId: string;
	businessName: string;
};

const RemoveBusinessModal = ({
	businessId,
	businessName,
	onRemoveBusiness
}: RemoveBusinessModalProps) => {
	const handleRemoveBusiness = useCallback(() => {
		onRemoveBusiness(businessId);
	}, [businessId, onRemoveBusiness]);

	return (
		<Dialog.Root>
			<Dialog.Trigger className="flex h-10 w-full items-start justify-center rounded-md transition-colors hover:bg-dm-black3.5">
				<RemoveButton />
			</Dialog.Trigger>

			<Dialog.Content className="gap-8 border-none">
				<Dialog.Title>Are you sure?</Dialog.Title>

				<Dialog.Description>
					Business <span className="text-orange">{businessName}</span>{' '}
					will be permanently removed from this list.
				</Dialog.Description>

				<Dialog.Footer>
					<Dialog.Close className="font-barlow font-semibold text-orange hover:underline">
						Cancel
					</Dialog.Close>

					<Dialog.Close asChild>
						<button
							type="button"
							onClick={handleRemoveBusiness}
							className="inline-flex items-center gap-1 rounded-md border border-dm-red px-3 py-1.5 font-semibold text-dm-red transition-colors hover:border-red hover:bg-red hover:text-dm-white"
						>
							<Trash />
							Confirm Deletion
						</button>
					</Dialog.Close>
				</Dialog.Footer>
			</Dialog.Content>
		</Dialog.Root>
	);
};

type StepCardProps = {
	step: PanelStepsType;
	order: number;
	current?: boolean;
};

const StepCard = ({ step, order, current }: StepCardProps) => {
	return (
		<div className="flex h-full w-[15.625rem] flex-shrink-0 flex-col justify-evenly gap-4 rounded-lg border border-dark-grey bg-dm-black3.5 p-5">
			<div
				data-step-current={current}
				className="flex items-center gap-2 text-sm data-[step-current=false]:text-dm-mid-grey data-[step-current=true]:text-dm-gold"
			>
				<span className="font-semibold">
					{current ? 'Current step' : 'Upcoming step'}
				</span>
				<span className="ml-4">Step {order}</span>
			</div>

			<div className="flex flex-nowrap gap-3 text-xs">
				<span className="min-w-max text-dm-mid-grey">
					Expected trigger day
				</span>
				<span className="line-clamp-1 text-white">
					{new Date(step.expectedDate).toLocaleDateString()}
				</span>
			</div>

			<div className="flex flex-nowrap gap-3 text-xs">
				<span className="min-w-max text-dm-mid-grey">
					Email subject
				</span>
				<span className="line-clamp-1 text-white">{step.subject}</span>
			</div>
		</div>
	);
};
